import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { Users, Building2, DollarSign, Edit, Mail, Phone, Loader2, Plus, X, ArrowRight, Globe, ChevronDown, ChevronUp } from "lucide-react";
import type { ClientGroup, Contact, Account, Deal, Partner } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { ActivityFeed } from "@/components/activity/activity-feed";
import { ClientGroupFormModal } from "@/components/client-group-form-modal";
import { AccountFormModal } from "@/components/account-form-modal";
import { ContactFormModal } from "@/components/contact-form-modal";
import { DealFormModal } from "@/components/deal-form-modal";
import { InlineTextField, InlineTextareaField } from "@/components/inline-edit";
import { LinkedDealsTable } from "@/components/linked-deals-table";
import { LinkedAccountsPanel } from "@/components/contacts/linked-accounts-panel";
import { EmploymentPanel } from "@/components/contacts/employment-panel";
import ContactRolesPanel from "@/components/contacts/contact-roles-panel";
import { AddressPanel } from "@/components/address-panel";
import { DirectorsPanel } from "@/components/accounts/directors-panel";
import { ShareholdersPanel } from "@/components/accounts/shareholders-panel";
import { BeneficialOwnersPanel } from "@/components/accounts/beneficial-owners-panel";
import { TrusteesPanel } from "@/components/accounts/trustees-panel";
import { TrustBeneficiariesPanel } from "@/components/accounts/trust-beneficiaries-panel";
import { SmsfMembersPanel } from "@/components/accounts/smsf-members-panel";
import { PartnersPanel } from "@/components/accounts/partners-panel";
import { ProprietorPanel } from "@/components/accounts/proprietor-panel";
import { RelatedEntitiesPanel } from "@/components/accounts/related-entities-panel";
import { AccountActivityPanel } from "@/components/accounts/account-activity-panel";
import { FacilitiesPanel } from "@/components/facilities-panel";
import { DealPartiesPanel } from "@/components/deal-parties-panel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useTabState } from "@/hooks/use-tab-state";
import { useNestedSelection } from "@/hooks/use-nested-selection";
import { useNestedTabState } from "@/hooks/use-nested-tab-state";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { buildBreadcrumbsFromUrl, buildNavigationUrl } from "@/lib/breadcrumbs";
import { FinancialsTabSidebarLayout } from "@/components/financial/layouts/financials-tab-sidebar-layout";
import { useEntityRecord, useFinancialPeriods } from "@/hooks/use-financial-data";
import { FinancialSummaryPanel } from "@/components/client-groups/financial-summary-panel";

const TRANSACTION_STAGES: Record<string, { label: string; color: string }> = {
  scenario: { label: "Scenario", color: "bg-slate-500" },
  deal: { label: "Deal", color: "bg-blue-500" },
  funded_loan: { label: "Funded Loan", color: "bg-green-500" },
};

export default function ClientGroupDetail() {
  const [, params] = useRoute("/client-groups/:id");
  const [location, navigate] = useLocation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [dealApplicantId, setDealApplicantId] = useState<string | null>(null);
  const [dealApplicantType, setDealApplicantType] = useState<"contact" | "account" | null>(null);

  // Use URL hash for tab persistence
  const [activeTab, setActiveTab] = useTabState("overview");

  const { toast } = useToast();

  // EDG-608: Nested selection state via URL hash for contacts and accounts
  const {
    selectedId: selectedContactId,
    setSelectedId: setSelectedContactId,
    subTab: contactSubTab,
    setSubTab: setContactSubTab,
  } = useNestedTabState({ parentTab: 'contacts', defaultSubTab: 'overview' });
  const {
    selectedId: selectedAccountId,
    setSelectedId: setSelectedAccountId,
    subTab: accountSubTab,
    setSubTab: setAccountSubTab,
  } = useNestedTabState({ parentTab: 'accounts', defaultSubTab: 'overview' });
  const {
    selectedId: selectedDealId,
    setSelectedId: setSelectedDealId,
    nested: dealSubTab,
    setNested: setDealSubTab,
  } = useNestedSelection<string>({ pageKey: 'clientGroupDeal', entityId: params?.id, defaultNested: 'overview' });

  // ISSUE-143: Collapsible state for contacts and accounts lists
  const [isContactsListOpen, setIsContactsListOpen] = useState(true);
  const [isAccountsListOpen, setIsAccountsListOpen] = useState(true);

  // Financial data for selected account (ISSUE-131 rework)
  const { data: selectedAccountEntityRecord, isPending: isEntityRecordPending, isLoadingError: isEntityRecordError } = useEntityRecord(selectedAccountId || undefined);
  const [selectedFinancialPeriod, setSelectedFinancialPeriod] = useState<any>(undefined);
  const { data: accountFinancialPeriods = [], isPending: isFinancialPeriodsPending, isLoadingError: isFinancialPeriodsError } = useFinancialPeriods(selectedAccountEntityRecord?.id);

  // Parse search parameters for dynamic breadcrumbs
  const searchParams = new URLSearchParams(location.split('?')[1] || '');

  const { data: group, isLoading } = useQuery<ClientGroup>({
    queryKey: ["/api/crm/client-groups", params?.id],
    enabled: !!params?.id,
  });

  // Fetch contacts linked to this client group
  const { data: contacts = [], isLoading: isLoadingContacts } = useQuery<Contact[]>({
    queryKey: ['/api/crm/contacts', { clientGroupId: params?.id }],
    enabled: !!params?.id,
  });

  // Fetch accounts linked to this client group
  const { data: accounts = [], isLoading: isLoadingAccounts } = useQuery<Account[]>({
    queryKey: ['/api/crm/accounts', { clientGroupId: params?.id }],
    enabled: !!params?.id,
  });

  // Fetch deals linked to this client group
  const { data: deals = [], isLoading: isLoadingDeals } = useQuery<Deal[]>({
    queryKey: ['/api/crm/deals', { clientGroupId: params?.id }],
    enabled: !!params?.id,
  });

  // Fetch referring partner if exists
  const { data: referringPartner } = useQuery<Partner>({
    queryKey: ['/api/crm/partners', group?.referringPartnerId],
    enabled: !!group?.referringPartnerId,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/crm/client-groups/${params?.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/client-groups"] });
      toast({
        title: "Client group deleted",
        description: "The client group has been successfully deleted.",
      });
      navigate("/client-groups");
    },
    onError: (error: Error) => {
      const isCascadePrevention =
        error.message.includes("foreign key constraint") ||
        error.message.includes("still referenced") ||
        error.message.includes("violates foreign key");

      if (isCascadePrevention) {
        const linkedContactsCount = contacts.length;
        const linkedAccountsCount = accounts.length;
        const linkedDealsCount = deals.length;
        const totalLinked = linkedContactsCount + linkedAccountsCount + linkedDealsCount;

        const items: string[] = [];
        if (linkedContactsCount > 0) items.push(`${linkedContactsCount} contact${linkedContactsCount > 1 ? "s" : ""}`);
        if (linkedAccountsCount > 0) items.push(`${linkedAccountsCount} account${linkedAccountsCount > 1 ? "s" : ""}`);
        if (linkedDealsCount > 0) items.push(`${linkedDealsCount} deal${linkedDealsCount > 1 ? "s" : ""}`);

        toast({
          title: "Cannot delete client group",
          description: `This group has ${items.join(", ")}. Please remove or reassign ${totalLinked > 1 ? "them" : "it"} before deleting the group.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to delete client group",
          variant: "destructive",
        });
      }
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
    setIsDeleteDialogOpen(false);
  };

  // Mutation handlers for inline edits (ISSUE-131: Add nested tab parity)
  const contactUpdateMutation = useMutation({
    mutationFn: async ({ contactId, updates }: { contactId: string; updates: Partial<Contact> }) => {
      const response = await apiRequest("PATCH", `/api/crm/contacts/${contactId}`, updates);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/crm/contacts', { clientGroupId: params?.id }] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update contact",
        variant: "destructive",
      });
    },
  });

  const accountUpdateMutation = useMutation({
    mutationFn: async ({ accountId, updates }: { accountId: string; updates: Partial<Account> }) => {
      const response = await apiRequest("PATCH", `/api/crm/accounts/${accountId}`, updates);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/crm/accounts', { clientGroupId: params?.id }] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update account",
        variant: "destructive",
      });
    },
  });

  const dealUpdateMutation = useMutation({
    mutationFn: async ({ dealId, updates }: { dealId: string; updates: Partial<Deal> }) => {
      const response = await apiRequest("PATCH", `/api/crm/deals/${dealId}`, updates);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/crm/deals', { clientGroupId: params?.id }] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update deal",
        variant: "destructive",
      });
    },
  });

  const handleContactFieldUpdate = async (field: keyof Contact, value: any): Promise<void> => {
    if (!selectedContactId) return;
    await contactUpdateMutation.mutateAsync({ contactId: selectedContactId, updates: { [field]: value } });
  };

  const handleAccountFieldUpdate = async (field: keyof Account, value: any): Promise<void> => {
    if (!selectedAccountId) return;
    await accountUpdateMutation.mutateAsync({ accountId: selectedAccountId, updates: { [field]: value } });
  };

  const handleDealFieldUpdate = async (field: keyof Deal, value: any): Promise<void> => {
    if (!selectedDealId) return;
    await dealUpdateMutation.mutateAsync({ dealId: selectedDealId, updates: { [field]: value } });
  };

  const handleNavItemClick = (href: string) => {
    const tabValue = href.replace("#", "");
    setActiveTab(tabValue);
  };

  // Helper functions for nested tab pattern
  const selectedContact = selectedContactId ? contacts.find(c => c.id === selectedContactId) : null;
  const selectedAccount = selectedAccountId ? accounts.find(a => a.id === selectedAccountId) : null;
  const selectedDeal = selectedDealId ? deals.find(d => d.id === selectedDealId) : null;

  // Issue #511: Stale entity detection — clear sessionStorage if entity no longer exists
  useEffect(() => {
    if (selectedContactId && contacts.length > 0 && !selectedContact) {
      setSelectedContactId(null);
    }
  }, [selectedContactId, contacts, selectedContact, setSelectedContactId]);

  // Issue #511: Scroll-restore for selected contact card
  const selectedCardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (selectedContactId && selectedContact && selectedCardRef.current) {
      selectedCardRef.current.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, [selectedContactId, selectedContact]);

  const handleContactSelect = (contactId: string) => {
    setSelectedContactId(contactId); // Resets sub-tab to "overview" automatically
  };

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccountId(accountId); // Resets sub-tab to "overview" automatically
  };

  const handleDealSelect = (dealId: string) => {
    setSelectedDealId(dealId);
    setDealSubTab("overview");
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading client group...</p>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-3 text-sm">
            The client group you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/client-groups")} size="sm">
            Back to Client Groups
          </Button>
        </div>
      </div>
    );
  }

  const breadcrumbs = buildBreadcrumbsFromUrl(
    searchParams,
    [
      { label: "Home", href: "/" },
      { label: "Client Groups", href: "/client-groups" },
      { label: group.groupName },
    ]
  );

  return (
    <>
      <DetailPageShell
        title={group.groupName}
        breadcrumbs={breadcrumbs}
        value={activeTab}
        onValueChange={setActiveTab}
        onNavItemClick={handleNavItemClick}
        navItems={[
          { label: "Overview", href: "#overview" },
          { label: "Contacts", href: "#contacts", count: contacts.length },
          { label: "Accounts", href: "#accounts", count: accounts.length },
          { label: "Deals", href: "#deals", count: deals.length },
          { label: "Communications", href: "#communications" },
        ]}
        linkedEntities={[
          { label: "Contacts", count: contacts.length, href: "#contacts" },
          { label: "Accounts", count: accounts.length, href: "#accounts" },
          { label: "Deals", count: deals.length, href: "#deals" },
        ]}
        tabs={[
          {
            value: "overview",
            label: "Overview",
            content: (
              <div id="overview" className="space-y-4">
                {/* Financial Summary Panel (F010) */}
                {params?.id && (
                  <FinancialSummaryPanel clientGroupId={params.id} />
                )}

                {/* Quick Stats Cards */}
                <div className="grid gap-3 md:grid-cols-3">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Total Contacts
                      </CardTitle>
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold" data-testid="text-contacts-count">
                        {isLoadingContacts ? "-" : contacts.length}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Individual contacts in this group
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Total Accounts
                      </CardTitle>
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold" data-testid="text-accounts-count">
                        {isLoadingAccounts ? "-" : accounts.length}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Business entities in this group
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold" data-testid="text-deals-count">
                        {isLoadingDeals ? "-" : deals.length}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Deals in progress
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Group Information */}
                <Card>
                  <CardHeader>
                    <CardTitle>Group Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Group Name
                      </p>
                      <p className="text-base">{group.groupName}</p>
                    </div>

                    {group.description && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">
                          Description
                        </p>
                        <p className="text-base" data-testid="text-group-description">{group.description}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Referring Partner
                      </p>
                      <p className="text-base" data-testid="text-referring-partner">
                        {group.referringPartnerId ? (
                          referringPartner ? (
                            <>
                              {referringPartner.partnerName}
                              {referringPartner.companyName && ` (${referringPartner.companyName})`}
                            </>
                          ) : (
                            "Loading..."
                          )
                        ) : (
                          <span className="text-muted-foreground">Not assigned</span>
                        )}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "contacts",
            label: `Contacts (${contacts.length})`,
            content: (
              <div id="contacts" className="space-y-2">
                {/* Master List */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
                    <CardTitle>Group Contacts</CardTitle>
                    <div className="flex gap-2">
                      {selectedContactId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedContactId(null)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Clear Selection
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => setIsContactModalOpen(true)}
                        data-testid="button-new-contact"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        New Contact
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingContacts ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : contacts.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-3">
                        <p className="text-sm text-muted-foreground text-center">
                          No contacts found in this group.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsContactModalOpen(true)}
                          data-testid="button-new-contact-empty"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add First Contact
                        </Button>
                      </div>
                    ) : (
                      <Collapsible
                        open={isContactsListOpen}
                        onOpenChange={setIsContactsListOpen}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm text-muted-foreground">
                            {contacts.length} contact{contacts.length !== 1 ? 's' : ''} in this group
                          </p>
                          <CollapsibleTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="p-2"
                            >
                              <span className="text-sm font-medium mr-2">
                                {isContactsListOpen ? 'Collapse' : 'Expand'}
                              </span>
                              {isContactsListOpen ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent forceMount>
                          <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {contacts.map((contact) => (
                              <div
                                key={contact.id}
                                ref={selectedContactId === contact.id ? selectedCardRef : undefined}
                                className={`flex items-center justify-between p-3 border rounded-md transition-colors ${
                                  selectedContactId === contact.id
                                    ? "bg-blue-50 dark:bg-blue-950 border-blue-300 dark:border-blue-700"
                                    : "hover:bg-accent"
                                }`}
                                data-testid={`card-contact-${contact.id}`}
                              >
                                <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => handleContactSelect(contact.id)}>
                                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Users className="h-5 w-5 text-primary" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium">
                                        {contact.firstName} {contact.lastName}
                                      </p>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const url = buildNavigationUrl(`/contacts/${contact.id}`, {
                                            from: 'client-group',
                                            fromId: group.id,
                                            fromLabel: group.groupName,
                                          });
                                          navigate(url);
                                        }}
                                        title="View full contact details"
                                        data-testid={`button-view-contact-${contact.id}`}
                                      >
                                        <ArrowRight className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {contact.contactType === "individual" ? "Individual" : "Business Contact"}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  {contact.email && (
                                    <div className="flex items-center gap-1">
                                      <Mail className="h-4 w-4" />
                                      <span>{contact.email}</span>
                                    </div>
                                  )}
                                  {contact.mobile && (
                                    <div className="flex items-center gap-1">
                                      <Phone className="h-4 w-4" />
                                      <span>{contact.mobile}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </CardContent>
                </Card>

                {/* Detail Sub-Tabs (when contact selected) */}
                {selectedContact && (
                  <Card>
                    <CardContent className="pt-2">
                      <div className="flex items-center justify-between mb-3">
                        <Tabs value={contactSubTab} onValueChange={setContactSubTab} className="flex-1">
                          <TabsList>
                            <TabsTrigger value="overview">Overview</TabsTrigger>
                            <TabsTrigger value="addresses">Addresses</TabsTrigger>
                            <TabsTrigger value="employment">Employment</TabsTrigger>
                            <TabsTrigger value="accounts">Accounts</TabsTrigger>
                            <TabsTrigger value="roles">Roles</TabsTrigger>
                            <TabsTrigger value="deals">Deals</TabsTrigger>
                            <TabsTrigger value="documents">Documents</TabsTrigger>
                            <TabsTrigger value="activity">Activity</TabsTrigger>
                          </TabsList>
                        </Tabs>
                        <Button
                          size="sm"
                          onClick={() => {
                            setDealApplicantId(selectedContact.id);
                            setDealApplicantType("contact");
                            setIsDealModalOpen(true);
                          }}
                          data-testid="button-create-deal-from-contact"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create Deal
                        </Button>
                      </div>
                      <Tabs value={contactSubTab} onValueChange={setContactSubTab}>
                        <TabsContent value="overview" className="space-y-2 mt-0">
                          {/* ISSUE-131: Full inline-edit fields matching contact-detail.tsx */}
                          <Card>
                            <CardContent className="pt-2">
                              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CORE DETAILS</h3>
                              <div className="grid grid-cols-2 gap-2">
                                <InlineTextField
                                  value={selectedContact.firstName}
                                  onSave={(value) => handleContactFieldUpdate("firstName", value)}
                                  label="First Name"
                                  placeholder="Enter first name"
                                />
                                <InlineTextField
                                  value={selectedContact.middleName}
                                  onSave={(value) => handleContactFieldUpdate("middleName", value || null)}
                                  label="Middle Name"
                                  placeholder="Optional"
                                />
                                <InlineTextField
                                  value={selectedContact.lastName}
                                  onSave={(value) => handleContactFieldUpdate("lastName", value)}
                                  label="Last Name"
                                  placeholder="Enter last name"
                                />
                                <InlineTextField
                                  value={selectedContact.preferredName}
                                  onSave={(value) => handleContactFieldUpdate("preferredName", value || null)}
                                  label="Preferred Name"
                                  placeholder="Optional"
                                />
                                <div className="space-y-1">
                                  <label className="text-sm font-medium text-muted-foreground">Contact Type</label>
                                  <div className="flex items-center h-9">
                                    <span className="text-sm">
                                      {selectedContact.contactType === "individual" ? "Individual" : "Business Contact"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardContent className="pt-2">
                              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CONTACT INFORMATION</h3>
                              <div className="grid grid-cols-2 gap-2">
                                <InlineTextField
                                  value={selectedContact.email}
                                  onSave={(value) => handleContactFieldUpdate("email", value || null)}
                                  label="Email"
                                  placeholder="Optional"
                                />
                                <div className="space-y-1">
                                  <InlineTextField
                                    value={selectedContact.mobile}
                                    onSave={(value) => handleContactFieldUpdate("mobile", value || null)}
                                    label="Mobile"
                                    placeholder="Optional"
                                  />
                                  {selectedContact.isOverseasMobile && selectedContact.mobile && (
                                    <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                                      <Globe className="h-3 w-3" />
                                      <span>International mobile</span>
                                    </div>
                                  )}
                                </div>
                                <InlineTextField
                                  value={selectedContact.phone}
                                  onSave={(value) => handleContactFieldUpdate("phone", value || null)}
                                  label="Phone"
                                  placeholder="Optional"
                                />
                              </div>
                            </CardContent>
                          </Card>
                        </TabsContent>

                        <TabsContent value="addresses" className="space-y-2 mt-0">
                          <AddressPanel
                            entityType="contact"
                            entityId={selectedContact.id}
                            data-testid="panel-nested-contact-addresses"
                          />
                        </TabsContent>

                        <TabsContent value="employment" className="space-y-2 mt-0">
                          <EmploymentPanel contactId={selectedContact.id} />
                        </TabsContent>

                        <TabsContent value="accounts" className="space-y-2 mt-0">
                          <LinkedAccountsPanel contact={selectedContact} />
                        </TabsContent>

                        <TabsContent value="roles" className="space-y-2 mt-0">
                          <ContactRolesPanel contactId={selectedContact.id} contactLabel={[selectedContact.firstName, selectedContact.lastName].filter(Boolean).join(' ')} />
                        </TabsContent>

                        <TabsContent value="deals" className="mt-0">
                          {/* ISSUE-131: Replace placeholder with actual LinkedDealsTable */}
                          <LinkedDealsTable
                            applicantId={selectedContact.id}
                            applicantType="contact"
                            sourceFrom={{ from: 'client-group', fromId: group.id, fromLabel: group.groupName }}
                          />
                        </TabsContent>

                        <TabsContent value="documents" className="mt-0">
                          {/* ISSUE-131: Documents placeholder - no panel exists yet */}
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            Document management coming soon
                          </div>
                        </TabsContent>

                        <TabsContent value="activity" className="mt-0">
                          {selectedAccount ? (
                            <AccountActivityPanel
                              accountId={selectedAccount.id}
                              testId="panel-nested-account-activity"
                            />
                          ) : null}
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
          {
            value: "accounts",
            label: `Accounts (${accounts.length})`,
            content: (
              <div id="accounts" className="space-y-2">
                {/* Master List */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
                    <CardTitle>Group Accounts</CardTitle>
                    <div className="flex gap-2">
                      {selectedAccountId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedAccountId(null)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Clear Selection
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => setIsAccountModalOpen(true)}
                        data-testid="button-new-account"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        New Account
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingAccounts ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : accounts.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-3">
                        <p className="text-sm text-muted-foreground text-center">
                          No accounts found in this group.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsAccountModalOpen(true)}
                          data-testid="button-new-account-empty"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add First Account
                        </Button>
                      </div>
                    ) : (
                      <Collapsible
                        open={isAccountsListOpen}
                        onOpenChange={setIsAccountsListOpen}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm text-muted-foreground">
                            {accounts.length} account{accounts.length !== 1 ? 's' : ''} in this group
                          </p>
                          <CollapsibleTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="p-2"
                            >
                              <span className="text-sm font-medium mr-2">
                                {isAccountsListOpen ? 'Collapse' : 'Expand'}
                              </span>
                              {isAccountsListOpen ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {accounts.map((account) => (
                              <div
                                key={account.id}
                                className={`flex items-center justify-between p-3 border rounded-md transition-colors ${
                                  selectedAccountId === account.id
                                    ? "bg-blue-50 dark:bg-blue-950 border-blue-300 dark:border-blue-700"
                                    : "hover:bg-accent"
                                }`}
                                data-testid={`card-account-${account.id}`}
                              >
                                <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => handleAccountSelect(account.id)}>
                                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Building2 className="h-5 w-5 text-primary" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium">{account.legalName}</p>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const url = buildNavigationUrl(`/accounts/${account.id}`, {
                                            from: 'client-group',
                                            fromId: group.id,
                                            fromLabel: group.groupName,
                                          });
                                          navigate(url);
                                        }}
                                        title="View full account details"
                                        data-testid={`button-view-account-${account.id}`}
                                      >
                                        <ArrowRight className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    {account.tradingName && account.tradingName !== account.legalName && (
                                      <p className="text-sm text-muted-foreground">
                                        Trading as: {account.tradingName}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">
                                    {account.entityType === "company" ? "Company" :
                                     account.entityType === "trust" ? "Trust" :
                                     account.entityType === "partnership" ? "Partnership" :
                                     account.entityType === "sole_trader" ? "Sole Trader" : "SMSF"}
                                  </Badge>
                                  {account.abn && (
                                    <span className="text-sm text-muted-foreground">
                                      ABN: {account.abn}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </CardContent>
                </Card>

                {/* Detail Sub-Tabs (when account selected) */}
                {selectedAccount && (
                  <Card>
                    <CardContent className="pt-2">
                      <div className="flex items-center justify-between mb-3">
                        <Tabs value={accountSubTab} onValueChange={setAccountSubTab} className="flex-1">
                          <TabsList>
                            <TabsTrigger value="overview">Overview</TabsTrigger>
                            <TabsTrigger value="structure">Structure</TabsTrigger>
                            <TabsTrigger value="addresses">Addresses</TabsTrigger>
                            <TabsTrigger value="financials">Financials</TabsTrigger>
                            <TabsTrigger value="deals">Deals</TabsTrigger>
                            <TabsTrigger value="related-entities">Related Entities</TabsTrigger>
                            <TabsTrigger value="documents">Documents</TabsTrigger>
                            <TabsTrigger value="activity">Activity</TabsTrigger>
                          </TabsList>
                        </Tabs>
                        <Button
                          size="sm"
                          onClick={() => {
                            setDealApplicantId(selectedAccount.id);
                            setDealApplicantType("account");
                            setIsDealModalOpen(true);
                          }}
                          data-testid="button-create-deal-from-account"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create Deal
                        </Button>
                      </div>
                      <Tabs value={accountSubTab} onValueChange={setAccountSubTab}>
                        <TabsContent value="overview" className="space-y-2 mt-0">
                          {/* ISSUE-131: Full inline-edit fields matching account-detail.tsx */}
                          <Card>
                            <CardContent className="pt-2">
                              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ACCOUNT DETAILS</h3>
                              <div className="grid grid-cols-2 gap-2">
                                <InlineTextField
                                  value={selectedAccount.legalName}
                                  onSave={(value) => handleAccountFieldUpdate("legalName", value)}
                                  label="Legal Name"
                                  placeholder="Enter legal name"
                                />
                                <InlineTextField
                                  value={selectedAccount.tradingName}
                                  onSave={(value) => handleAccountFieldUpdate("tradingName", value || null)}
                                  label="Trading Name"
                                  placeholder="Optional"
                                />
                                <div className="space-y-1">
                                  <label className="text-sm font-medium text-muted-foreground">Entity Type</label>
                                  <div className="flex items-center h-9">
                                    <Badge variant="outline">
                                      {selectedAccount.entityType === "company" ? "Company" :
                                       selectedAccount.entityType === "trust" ? "Trust" :
                                       selectedAccount.entityType === "partnership" ? "Partnership" :
                                       selectedAccount.entityType === "sole_trader" ? "Sole Trader" : "SMSF"}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardContent className="pt-2">
                              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">REGISTRATION</h3>
                              <div className="grid grid-cols-2 gap-2">
                                <InlineTextField
                                  value={selectedAccount.abn}
                                  onSave={(value) => handleAccountFieldUpdate("abn", value || null)}
                                  label="ABN"
                                  placeholder="Optional"
                                />
                                {selectedAccount.entityType === "company" && (
                                  <InlineTextField
                                    value={selectedAccount.acn}
                                    onSave={(value) => handleAccountFieldUpdate("acn", value || null)}
                                    label="ACN"
                                    placeholder="Optional"
                                  />
                                )}
                              </div>
                            </CardContent>
                          </Card>

                          {/* Entity-Specific Fields Section */}
                          {selectedAccount.entityType === "trust" && (
                            <Card>
                              <CardContent className="pt-2">
                                <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">TRUST DETAILS</h3>
                                <div className="grid grid-cols-2 gap-2">
                                  <InlineTextField
                                    value={selectedAccount.trustType}
                                    onSave={(value) => handleAccountFieldUpdate("trustType", value || null)}
                                    label="Trust Type"
                                    placeholder="e.g. Family, Unit, Discretionary"
                                  />
                                  <div className="space-y-1">
                                    <label className="text-sm font-medium text-muted-foreground">Trustee Type</label>
                                    <div className="flex items-center h-9">
                                      <span className="text-sm">
                                        {selectedAccount.trusteeType === "individual" ? "Individual" :
                                         selectedAccount.trusteeType === "company" ? "Company" : "-"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {/* SMSF details shown when trust has smsfComplyingStatus */}
                          {selectedAccount.entityType === "trust" && selectedAccount.smsfComplyingStatus && (
                            <Card>
                              <CardContent className="pt-2">
                                <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">SMSF DETAILS</h3>
                                <div className="grid grid-cols-2 gap-2">
                                  <InlineTextField
                                    value={selectedAccount.abn}
                                    onSave={(value) => handleAccountFieldUpdate("abn", value || null)}
                                    label="Fund ABN"
                                    placeholder="Optional"
                                  />
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          <Card>
                            <CardContent className="pt-2">
                              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">BUSINESS PROFILE</h3>
                              <div className="grid grid-cols-2 gap-2">
                                <InlineTextField
                                  value={selectedAccount.industry}
                                  onSave={(value) => handleAccountFieldUpdate("industry", value || null)}
                                  label="Industry"
                                  placeholder="Optional"
                                />
                                <InlineTextField
                                  value={selectedAccount.anzsicCode}
                                  onSave={(value) => handleAccountFieldUpdate("anzsicCode", value || null)}
                                  label="ANZSIC Code"
                                  placeholder="Optional"
                                />
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardContent className="pt-2">
                              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CONTACT INFORMATION</h3>
                              <div className="grid grid-cols-2 gap-2">
                                <InlineTextField
                                  value={selectedAccount.email}
                                  onSave={(value) => handleAccountFieldUpdate("email", value || null)}
                                  label="Email"
                                  placeholder="Optional"
                                />
                                <InlineTextField
                                  value={selectedAccount.phone}
                                  onSave={(value) => handleAccountFieldUpdate("phone", value || null)}
                                  label="Phone"
                                  placeholder="Optional"
                                />
                                <InlineTextField
                                  value={selectedAccount.website}
                                  onSave={(value) => handleAccountFieldUpdate("website", value || null)}
                                  label="Website"
                                  placeholder="Optional"
                                />
                              </div>
                            </CardContent>
                          </Card>
                        </TabsContent>

                        <TabsContent value="structure" className="space-y-2 mt-0">
                          {/* Directors - Only for Companies */}
                          {selectedAccount.entityType === "company" && (
                            <DirectorsPanel account={selectedAccount} />
                          )}

                          {/* Shareholders - Only for Companies */}
                          {selectedAccount.entityType === "company" && (
                            <ShareholdersPanel account={selectedAccount} />
                          )}

                          {/* Partners - Only for Partnerships */}
                          {selectedAccount.entityType === "partnership" && (
                            <PartnersPanel account={selectedAccount} />
                          )}

                          {/* Proprietor - Only for Sole Traders */}
                          {selectedAccount.entityType === "sole_trader" && (
                            <ProprietorPanel account={selectedAccount} />
                          )}

                          {/* Trustees - Only for Trusts */}
                          {selectedAccount.entityType === "trust" && (
                            <TrusteesPanel account={selectedAccount} />
                          )}

                          {/* Beneficiaries - Only for Trusts */}
                          {selectedAccount.entityType === "trust" && (
                            <TrustBeneficiariesPanel account={selectedAccount} />
                          )}

                          {/* SMSF Members - Only for SMSFs */}
                          {selectedAccount.entityType === "trust" && selectedAccount.trustType === "smsf" && (
                            <SmsfMembersPanel account={selectedAccount} />
                          )}

                          {/* Beneficial Owners - For Companies, Trusts, and Partnerships */}
                          {(selectedAccount.entityType === "company" || selectedAccount.entityType === "trust" || selectedAccount.entityType === "partnership") && (
                            <BeneficialOwnersPanel account={selectedAccount} />
                          )}
                        </TabsContent>

                        <TabsContent value="addresses" className="space-y-2 mt-0">
                          <AddressPanel
                            entityType="account"
                            entityId={selectedAccount.id}
                            data-testid="panel-nested-account-addresses"
                          />
                        </TabsContent>

                        <TabsContent value="financials" className="mt-0">
                          <FinancialsTabSidebarLayout
                            accountId={selectedAccountId || ''}
                            entityRecord={selectedAccountEntityRecord}
                            periods={accountFinancialPeriods}
                            isLoading={isEntityRecordPending || (!!selectedAccountEntityRecord?.id && isFinancialPeriodsPending)}
                            isError={isEntityRecordError || isFinancialPeriodsError}
                            selectedPeriod={selectedFinancialPeriod}
                            onPeriodSelect={setSelectedFinancialPeriod}
                            onRecordCreated={(_record) => {
                              // No toast needed in nested context - just refresh
                            }}
                          />
                        </TabsContent>

                        <TabsContent value="deals" className="mt-0">
                          {/* ISSUE-131: Replace placeholder with actual LinkedDealsTable */}
                          <LinkedDealsTable
                            applicantId={selectedAccount.id}
                            applicantType="account"
                            sourceFrom={{ from: 'client-group', fromId: group.id, fromLabel: group.groupName }}
                          />
                        </TabsContent>

                        <TabsContent value="related-entities" className="space-y-2 mt-0">
                          <RelatedEntitiesPanel
                            accountId={selectedAccount.id}
                            entityType={selectedAccount.entityType}
                            trustType={selectedAccount.trustType ?? undefined}
                          />
                        </TabsContent>

                        <TabsContent value="documents" className="mt-0">
                          {/* ISSUE-131: Documents placeholder - no panel exists yet */}
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            Document management coming soon
                          </div>
                        </TabsContent>

                        <TabsContent value="activity" className="mt-0">
                          {/* ISSUE-131: Activity placeholder - no panel exists yet */}
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            Activity feed coming soon
                          </div>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
          {
            value: "deals",
            label: `Deals (${deals.length})`,
            content: (
              <div id="deals" className="space-y-2">
                {/* Master List */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
                    <CardTitle>Group Deals</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => setIsDealModalOpen(true)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Create Deal
                      </Button>
                      {selectedDealId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedDealId(null)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Clear Selection
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingDeals ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : deals.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No deals found for this group. Deals will appear here when
                        applicants from this group have active deals.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {deals.map((deal) => {
                          const stageInfo = TRANSACTION_STAGES[deal.transactionStage] || { label: deal.transactionStage, color: "bg-gray-500" };
                          return (
                            <div
                              key={deal.id}
                              className={`flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${
                                selectedDealId === deal.id
                                  ? "bg-blue-50 dark:bg-blue-950 border-blue-300 dark:border-blue-700"
                                  : "hover:bg-accent"
                              }`}
                              onClick={() => handleDealSelect(deal.id)}
                              data-testid={`card-deal-${deal.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  <DollarSign className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium">{deal.dealName}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {deal.dealType === "equipment" ? "Equipment Finance" :
                                     deal.dealType === "property" ? "Property Finance" :
                                     deal.dealType === "working_capital" ? "Working Capital" :
                                     deal.dealType === "residential" ? "Residential" :
                                     deal.dealType === "business_acquisition" ? "Business Acquisition" :
                                     deal.dealType === "smsf_finance" ? "SMSF Finance" :
                                     deal.dealType === "development_finance" ? "Development Finance" : "Other"}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <p className="font-semibold text-primary">
                                    ${deal.amountRequested ? (Number(deal.amountRequested) / 1000).toFixed(0) + "k" : "-"}
                                  </p>
                                  {deal.lenderName && (
                                    <p className="text-sm text-muted-foreground">{deal.lenderName}</p>
                                  )}
                                </div>
                                <Badge className={`${stageInfo.color} text-white`}>
                                  {stageInfo.label}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Detail Sub-Tabs (when deal selected) */}
                {selectedDeal && (
                  <Card>
                    <CardContent className="pt-2">
                      <Tabs value={dealSubTab} onValueChange={setDealSubTab}>
                        <TabsList className="mb-3">
                          <TabsTrigger value="overview">Overview</TabsTrigger>
                          <TabsTrigger value="facilities">Facilities</TabsTrigger>
                          <TabsTrigger value="securities">Securities</TabsTrigger>
                          <TabsTrigger value="financials">Financials</TabsTrigger>
                          <TabsTrigger value="applicants">Applicants</TabsTrigger>
                          <TabsTrigger value="stage-history">Stage History</TabsTrigger>
                          <TabsTrigger value="documents">Documents</TabsTrigger>
                          <TabsTrigger value="activity">Activity</TabsTrigger>
                        </TabsList>

                        <TabsContent value="overview" className="space-y-2 mt-0">
                          {/* ISSUE-131: Full inline-edit fields matching deal-detail.tsx */}
                          <Card>
                            <CardContent className="pt-2">
                              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">DEAL INFORMATION</h3>
                              <div className="grid grid-cols-2 gap-2">
                                <InlineTextField
                                  value={selectedDeal.dealName}
                                  onSave={(value) => handleDealFieldUpdate("dealName", value)}
                                  label="Deal Name"
                                  placeholder="Enter deal name"
                                />
                                <div className="space-y-1">
                                  <label className="text-sm font-medium text-muted-foreground">Deal Type</label>
                                  <div className="flex items-center h-9">
                                    <span className="text-sm">
                                      {selectedDeal.dealType === "equipment" ? "Equipment Finance" :
                                       selectedDeal.dealType === "property" ? "Property Finance" :
                                       selectedDeal.dealType === "working_capital" ? "Working Capital" :
                                       selectedDeal.dealType === "residential" ? "Residential" :
                                       selectedDeal.dealType === "business_acquisition" ? "Business Acquisition" :
                                       selectedDeal.dealType === "smsf_finance" ? "SMSF Finance" :
                                       selectedDeal.dealType === "development_finance" ? "Development Finance" : "Other"}
                                    </span>
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <label className="text-sm font-medium text-muted-foreground">Amount Requested</label>
                                  <div className="flex items-center h-9">
                                    <span className="text-sm font-semibold text-primary">
                                      ${selectedDeal.amountRequested ? Number(selectedDeal.amountRequested).toLocaleString() : "-"}
                                    </span>
                                  </div>
                                  <p className="text-xs text-muted-foreground">(Auto-aggregated from facilities)</p>
                                </div>
                                <InlineTextField
                                  value={selectedDeal.lenderName}
                                  onSave={(value) => handleDealFieldUpdate("lenderName", value || null)}
                                  label="Lender"
                                  placeholder="Optional"
                                />
                                <div className="space-y-1">
                                  <label className="text-sm font-medium text-muted-foreground">Transaction Stage</label>
                                  <div className="flex items-center h-9">
                                    <Badge className={`${TRANSACTION_STAGES[selectedDeal.transactionStage || "scenario"]?.color || "bg-gray-500"} text-white`}>
                                      {TRANSACTION_STAGES[selectedDeal.transactionStage || "scenario"]?.label || selectedDeal.transactionStage || "Scenario"}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardContent className="pt-2">
                              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">NOTES</h3>
                              <InlineTextareaField
                                value={selectedDeal.notes}
                                onSave={(value) => handleDealFieldUpdate("notes", value || null)}
                                label="Deal Notes"
                                placeholder="Optional - Describe the deal purpose and key details"
                              />
                            </CardContent>
                          </Card>
                        </TabsContent>

                        <TabsContent value="facilities" className="mt-0">
                          <FacilitiesPanel dealId={selectedDeal.id} />
                        </TabsContent>

                        <TabsContent value="securities" className="mt-0">
                          {/* ISSUE-131: Securities placeholder - DealSecuritiesTab panel not implemented yet */}
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            Securities for this deal will appear here
                          </div>
                        </TabsContent>

                        <TabsContent value="financials" className="mt-0">
                          {/* ISSUE-131: Financials placeholder - no specific panel for deal financials yet */}
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            Financial analysis coming soon
                          </div>
                        </TabsContent>

                        <TabsContent value="applicants" className="mt-0">
                          {/* ISSUE-131: Already using DealPartiesPanel (actual panel) */}
                          <DealPartiesPanel dealId={selectedDeal.id} />
                        </TabsContent>

                        <TabsContent value="stage-history" className="mt-0">
                          {/* ISSUE-131: Stage history placeholder - StageHistoryPanel not implemented yet */}
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            Stage history for this deal will appear here
                          </div>
                        </TabsContent>

                        <TabsContent value="documents" className="mt-0">
                          {/* ISSUE-131: Documents placeholder - no panel exists yet */}
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            Document management coming soon
                          </div>
                        </TabsContent>

                        <TabsContent value="activity" className="mt-0">
                          {/* ISSUE-131: Activity placeholder - no panel exists yet */}
                          <div className="text-center py-8 text-sm text-muted-foreground">
                            Activity feed coming soon
                          </div>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
          {
            value: "communications",
            label: "Communications",
            content: (
              <div id="communications" className="space-y-2">
                {params?.id && (
                  <ActivityFeed
                    entityType="client_group"
                    entityId={params.id}
                    lens="email"
                    testId="panel-client-group-communications"
                  />
                )}
              </div>
            ),
          },
        ]}
        rightPanel={
          <div className="space-y-4">
            {/* Key Dates - MUST be first (metadata) */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY DATES</h3>
              <div className="space-y-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">
                    {group.createdAt
                      ? new Date(group.createdAt).toLocaleDateString("en-AU")
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Modified</p>
                  <p className="font-medium">
                    {group.updatedAt
                      ? new Date(group.updatedAt).toLocaleDateString("en-AU")
                      : "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">QUICK STATS</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contacts:</span>
                  <span className="font-medium">{contacts.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Accounts:</span>
                  <span className="font-medium">{accounts.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deals:</span>
                  <span className="font-medium">{deals.length}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ACTIONS</h3>
              <div className="space-y-2">
                <Button
                  size="sm"
                  className="w-full justify-start text-xs"
                  variant="outline"
                  onClick={() => setIsEditModalOpen(true)}
                  data-testid="button-edit-group"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit Group
                </Button>
                <Button
                  size="sm"
                  className="w-full justify-start text-xs"
                  variant="outline"
                  onClick={() => setIsContactModalOpen(true)}
                  data-testid="button-add-contact"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Contact
                </Button>
                <Button
                  size="sm"
                  className="w-full justify-start text-xs"
                  variant="outline"
                  onClick={() => setIsAccountModalOpen(true)}
                  data-testid="button-add-account"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Account
                </Button>
              </div>
            </div>
          </div>
        }
      />

      <ClientGroupFormModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        clientGroup={group}
        onDelete={() => navigate("/client-groups")}
      />

      <ContactFormModal
        open={isContactModalOpen}
        onOpenChange={setIsContactModalOpen}
        defaultClientGroupId={group.id}
        onContactCreated={(contact) => {
          queryClient.invalidateQueries({ queryKey: ['/api/crm/contacts', { clientGroupId: params?.id }] });
          toast({
            title: "Contact created",
            description: `${contact.firstName} ${contact.lastName} has been added to this group.`,
          });
          setIsContactModalOpen(false);
        }}
      />

      <AccountFormModal
        open={isAccountModalOpen}
        onOpenChange={setIsAccountModalOpen}
        defaultClientGroupId={group.id}
        onAccountCreated={(account) => {
          queryClient.invalidateQueries({ queryKey: ['/api/crm/accounts', { clientGroupId: params?.id }] });
          toast({
            title: "Account created",
            description: `${account.legalName} has been added to this group.`,
          });
          setIsAccountModalOpen(false);
        }}
      />

      <DealFormModal
        open={isDealModalOpen}
        onOpenChange={setIsDealModalOpen}
        applicantId={dealApplicantId || undefined}
        applicantType={dealApplicantType || undefined}
        contextClientGroupId={params?.id} // ISSUE-123: Pass client group context for filtering and locking
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="dialog-delete-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{group?.groupName}"? This action cannot be undone.
              All associated contacts, accounts, and deals will lose their group association.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="button-confirm-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
